import { HttpService, accessTokenProvider } from "@common";
import { IDevice, IApiKey, IUser } from "@common/models";

export interface IRefreshTokenResponse {
  isKnownDevice: boolean;
  refreshToken: string;
}

export interface IAccessTokenResponse {
  accessToken: string;
}

class UserService {
  private authUrl: string;
  private http: HttpService;

  constructor() {
    this.authUrl = `${urls.auth}/login`;
    this.http = new HttpService(accessTokenProvider);
  }

  public getProfile(): Promise<IUser> {
    const userInfoUrl = `${urls.auth}/users/me`;

    return this.http.fetch(userInfoUrl).then(res => res.json());
  }

  public getApiKeys(): Promise<IApiKey[]> {
    const url = `${urls.auth}/apikeys`;
    return this.http.fetch(url).then(res => res.json());
  }

  public loginWithToken(
    refreshToken: string,
    deviceid: string
  ): Promise<IAccessTokenResponse> {
    if (!refreshToken) {
      return Promise.reject("RefreshToken is missing");
    }

    return fetch(this.authUrl, {
      headers: {
        deviceid,
        refreshToken
      },
      method: "POST"
    })
      .then(res => res.json())
      .then(json => ({ accessToken: json.access_token }));
  }

  public createNewApiKey(userId: string): Promise<IApiKey[]> {
    const url = `${urls.auth}/apikeys/${userId}`;

    return this.http
      .fetch(url,  {
        method: "POST",
      })
      .then(res => res.json())
  }

  public editApiKey(oldName: string, newName: string): Promise<IUser> {
    const url = `${urls.auth}/apikeys`;

    return this.http
      .fetch(url, {
        method: "PUT",
        body: JSON.stringify({
          oldName,
          newName
        })
      })
      .then(res => res.json())
  }

  public deleteApiKey(userId: string, apiKeyName: string): Promise<IApiKey[]> {
    const url = `${urls.auth}/apiKeys/${userId}/${apiKeyName}`;

    return this.http
      .fetch(url, {
        method: "DELETE"
      })
      .then(res => res.json())
  }

  public loginWithPassword(
    username: string,
    password: string,
    deviceId: string,
    userAgent: string
  ): Promise<IRefreshTokenResponse> {
    if (!username) {
      return Promise.reject("Username is missing");
    }

    if (!password) {
      return Promise.reject("Password is missing");
    }

    if (!deviceId) {
      return Promise.reject("DeviceId is missing");
    }

    if (!userAgent) {
      return Promise.reject("UserAgent is missing");
    }

    const encodedAuth = btoa(`${username}:${password}`);
    return fetch(this.authUrl, {
      headers: {
        Authorization: `Basic ${encodedAuth}`,
        "Content-Type": "application/x-www-form-urlencoded",
        deviceid: deviceId,
        customUserAgent: userAgent
      },
      method: "POST"
    })
      .then(res => res.json())
      .then(json => ({
        isKnownDevice: json.isKnownDevice,
        refreshToken: json.refresh_token
      }));
  }

  public getDevices(): Promise<IDevice[]> {
    return new Promise((resolve, reject) => {
      const url = `${urls.auth}/devices`;

      this.http
        .fetch(url)
        .then(res => res.json())
        .then(resolve)
        .catch(reject);
    });
  }

  public removeDevice(userId: string, deviceId: string): Promise<IUser> {
    const url = `${urls.auth}/devices/${btoa(deviceId)}/${userId}`;
    return this.http.fetch(url, { method: "DELETE" }).then(res => res.json());
  }

  public resetPassword(username: string): Promise<string> {
    const url = `${urls.auth}/users/resetpassword`;
    return this.http
      .fetch(url, { method: "POST", body: JSON.stringify({ username }) })
      .then(res => res.json());
  }

  public changePassword = (password, newPassword): Promise<string> => {
    const url = `${urls.auth}/users/changepassword`;
    return this.http
      .fetch(url, {
        method: "POST",
        body: JSON.stringify({ password, newPassword })
      })
      .then(res => res.json());
  };
}

export default UserService;
